import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function MonthlyBudgetPanel({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [newExpense, setNewExpense] = useState({ name: '', amount: '' });
  const [newIncome, setNewIncome] = useState({ name: '', amount: '' });

  useEffect(() => { fetchData(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    try {
      const res = await apiFetch('/api/monthly-budget');
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function saveConfig(updates) {
    setSaving(true);
    const newConfig = { ...data.config, ...updates };
    try {
      await apiFetch('/api/monthly-budget', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newConfig),
      });
      await fetchData();
    } catch (err) { console.error(err); }
    finally { setSaving(false); setEditing(null); }
  }

  function startEdit(field, value) {
    setEditing(field);
    setEditValue(String(value || 0));
  }

  function commitEdit(field) {
    saveConfig({ [field]: parseFloat(editValue) || 0 });
  }

  function handleKey(e, field) {
    if (e.key === 'Enter') commitEdit(field);
    if (e.key === 'Escape') setEditing(null);
  }

  function addExpense(e) {
    e.preventDefault();
    if (!newExpense.name.trim()) return;
    const expenses = [...(data.config.expenses || []), { name: newExpense.name.trim(), amount: parseFloat(newExpense.amount) || 0 }];
    saveConfig({ expenses });
    setNewExpense({ name: '', amount: '' });
  }

  function removeExpense(idx) {
    const expenses = data.config.expenses.filter((_, i) => i !== idx);
    saveConfig({ expenses });
  }

  function updateExpenseAmount(idx, value) {
    const expenses = data.config.expenses.map((e, i) => i === idx ? { ...e, amount: parseFloat(value) || 0 } : e);
    saveConfig({ expenses });
  }

  function addIncome(e) {
    e.preventDefault();
    if (!newIncome.name.trim()) return;
    const incomeItems = [...(data.config.incomeItems || []), { name: newIncome.name.trim(), amount: parseFloat(newIncome.amount) || 0 }];
    saveConfig({ incomeItems });
    setNewIncome({ name: '', amount: '' });
  }

  function removeIncome(idx) {
    const incomeItems = data.config.incomeItems.filter((_, i) => i !== idx);
    saveConfig({ incomeItems });
  }

  function updateIncomeAmount(idx, value) {
    const incomeItems = data.config.incomeItems.map((e, i) => i === idx ? { ...e, amount: parseFloat(value) || 0 } : e);
    saveConfig({ incomeItems });
  }

  function fmt(val) {
    return parseFloat(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function fmtD(val) {
    return parseFloat(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  if (loading || !data) return <section className="card"><h2>Monthly Gains vs Expenses</h2><p className="loading">Loading...</p></section>;

  const rowStyle = { display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #22232d', fontSize: 13, gap: 8 };
  const labelStyle = { flex: 1, minWidth: 0 };
  const editColStyle = { width: 100, textAlign: 'right', flexShrink: 0 };
  const gainColStyle = { width: 110, textAlign: 'right', flexShrink: 0, color: '#22c55e', fontWeight: 600 };
  const inputStyle = { width: '100%', background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 4, padding: '3px 6px', fontSize: 13, textAlign: 'right' };

  function EditableField({ field, value, suffix }) {
    if (editing === field) {
      return (
        <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => handleKey(e, field)} onBlur={() => commitEdit(field)}
          autoFocus disabled={saving} style={inputStyle} />
      );
    }
    return (
      <span style={{ cursor: 'pointer', color: '#8b5cf6' }} onClick={() => startEdit(field, value)} title="Click to edit">
        {suffix ? `${value}${suffix}` : fmt(value)}
      </span>
    );
  }

  function SummaryBox({ label, value }) {
    const pos = value >= 0;
    return (
      <div style={{ flex: 1, padding: '8px 10px', background: pos ? '#16332a' : '#331616', borderRadius: 8, textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: pos ? '#22c55e' : '#ef4444' }}>{pos ? '+' : ''}{fmtD(value)}</div>
        <div style={{ fontSize: 10, color: '#71717a', lineHeight: 1.3 }}>{label}</div>
      </div>
    );
  }

  return (
    <section className="card cf-section">
      <h2>Monthly Gains vs Expenses</h2>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <SummaryBox label="Net Monthly" value={data.netMonthlyWithIncome} />
        <SummaryBox label="Cashflow" value={data.cashflowWithIncome} />
      </div>

      {/* Column headers */}
      <div style={{ display: 'flex', padding: '4px 0', fontSize: 11, color: '#52525b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        <div style={labelStyle}>Source</div>
        <div style={editColStyle}>Rate / Input</div>
        <div style={gainColStyle}>Monthly Gain</div>
      </div>

      <h3 style={{ fontSize: 12, color: '#22c55e', margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>Gains</h3>

      {/* 401k */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>401k</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>{data.name401k} — {fmt(data.balance401k)}</div>
        </div>
        <div style={editColStyle}><EditableField field="return401kPercent" value={data.config.return401kPercent} suffix="% /yr" /></div>
        <div style={gainColStyle}>{fmtD(data.monthly401k)}</div>
      </div>

      {/* Brokerage */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Brokerage</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>{data.nameBrokerage} — {fmt(data.balanceBrokerage)}</div>
        </div>
        <div style={editColStyle}><EditableField field="returnBrokeragePercent" value={data.config.returnBrokeragePercent} suffix="% /yr" /></div>
        <div style={gainColStyle}>{fmtD(data.monthlyBrokerage)}</div>
      </div>

      {/* Settlement */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Settlement</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>{data.settlementName} — {fmt(data.settlementBalance)} @ 5% /yr</div>
        </div>
        <div style={editColStyle}></div>
        <div style={gainColStyle}>{fmtD(data.monthlySettlement)}</div>
      </div>

      {/* Airbnb */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Airbnb</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>Monthly income</div>
        </div>
        <div style={editColStyle}><EditableField field="airbnbMonthly" value={data.config.airbnbMonthly} /></div>
        <div style={gainColStyle}>{fmtD(data.monthlyAirbnb)}</div>
      </div>

      {/* Sherman — Equity */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>14523 S Sherman — Equity</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>{data.shermanName} — {fmt(data.shermanValue)}</div>
        </div>
        <div style={editColStyle}><EditableField field="shermanEquityGainPercent" value={data.config.shermanEquityGainPercent} suffix="% /yr" /></div>
        <div style={gainColStyle}>{fmtD(data.monthlyShermanEquity)}</div>
      </div>

      {/* Sherman — Loan Payoff */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>14523 S Sherman — Loan Payoff</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>Principal per month</div>
        </div>
        <div style={editColStyle}><EditableField field="shermanLoanPayoffPerMonth" value={data.config.shermanLoanPayoffPerMonth} /></div>
        <div style={gainColStyle}>{fmtD(data.config.shermanLoanPayoffPerMonth)}</div>
      </div>

      {/* Sheffield — Equity */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>3541 N Sheffield — Equity</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>{data.sheffieldName} — {fmt(data.sheffieldValue)}</div>
        </div>
        <div style={editColStyle}><EditableField field="sheffieldEquityGainPercent" value={data.config.sheffieldEquityGainPercent} suffix="% /yr" /></div>
        <div style={gainColStyle}>{fmtD(data.monthlySheffieldEquity)}</div>
      </div>

      {/* Sheffield — Loan Payoff */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>3541 N Sheffield — Loan Payoff</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>Principal per month</div>
        </div>
        <div style={editColStyle}><EditableField field="sheffieldLoanPayoffPerMonth" value={data.config.sheffieldLoanPayoffPerMonth} /></div>
        <div style={gainColStyle}>{fmtD(data.config.sheffieldLoanPayoffPerMonth)}</div>
      </div>

      {/* Manual gains */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Other Monthly Gains</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>Manual entry</div>
        </div>
        <div style={editColStyle}><EditableField field="manualMonthlyGains" value={data.config.manualMonthlyGains} /></div>
        <div style={gainColStyle}>{fmtD(data.config.manualMonthlyGains)}</div>
      </div>

      {/* Expenses section */}
      <h3 style={{ fontSize: 12, color: '#ef4444', margin: '16px 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>Monthly Expenses</h3>
      {(data.config.expenses || []).map((exp, idx) => (
        <div key={idx} style={rowStyle}>
          <div style={labelStyle}>
            <span style={{ fontWeight: 600, color: '#e4e4e7' }}>{exp.name}</span>
          </div>
          <div style={{ ...editColStyle, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
            {editing === `expense_${idx}` ? (
              <input type="number" value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { updateExpenseAmount(idx, editValue); setEditing(null); } if (e.key === 'Escape') setEditing(null); }}
                onBlur={() => { updateExpenseAmount(idx, editValue); setEditing(null); }}
                autoFocus style={inputStyle} />
            ) : (
              <span style={{ cursor: 'pointer', color: '#8b5cf6' }}
                onClick={() => startEdit(`expense_${idx}`, exp.amount)}>
                {fmt(exp.amount)}
              </span>
            )}
            <button onClick={() => removeExpense(idx)}
              style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 16, padding: 0 }}>&times;</button>
          </div>
          <div style={{ ...gainColStyle, color: '#ef4444' }}>-{fmtD(exp.amount)}</div>
        </div>
      ))}

      {/* Add expense */}
      <form onSubmit={addExpense} style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <input type="text" value={newExpense.name} onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
          placeholder="Expense name..." style={{ flex: 1, background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        <input type="number" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
          placeholder="$/mo" step="0.01" style={{ width: 90, background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        <button type="submit" className="manage-btn" disabled={!newExpense.name.trim()} style={{ fontSize: 12 }}>+ Add</button>
      </form>

      {/* Debt / Interest section */}
      <h3 style={{ fontSize: 12, color: '#f97316', margin: '16px 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>Debt Interest</h3>

      {/* Credit Card Debt */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Personal Credit Card Debt</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>{data.creditCardAccountCount} card{data.creditCardAccountCount !== 1 ? 's' : ''} — {fmt(data.creditCardDebt)} total</div>
        </div>
        <div style={editColStyle}><EditableField field="creditCardInterestPercent" value={data.config.creditCardInterestPercent} suffix="% /yr" /></div>
        <div style={{ ...gainColStyle, color: '#ef4444' }}>-{fmtD(data.monthlyCreditCardInterest)}</div>
      </div>

      {/* HELOC */}
      <div style={rowStyle}>
        <div style={labelStyle}>
          <div style={{ fontWeight: 600, color: '#e4e4e7' }}>HELOC</div>
          <div style={{ fontSize: 11, color: '#71717a' }}>{data.helocName} — {fmt(data.helocDebt)} owed</div>
        </div>
        <div style={editColStyle}><EditableField field="helocInterestPercent" value={data.config.helocInterestPercent} suffix="% /yr" /></div>
        <div style={{ ...gainColStyle, color: '#ef4444' }}>-{fmtD(data.monthlyHelocInterest)}</div>
      </div>

      {/* Income section */}
      <h3 style={{ fontSize: 12, color: '#3b82f6', margin: '16px 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>Income</h3>
      {(data.config.incomeItems || []).map((inc, idx) => (
        <div key={idx} style={rowStyle}>
          <div style={labelStyle}>
            <span style={{ fontWeight: 600, color: '#e4e4e7' }}>{inc.name}</span>
          </div>
          <div style={{ ...editColStyle, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
            {editing === `income_${idx}` ? (
              <input type="number" value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { updateIncomeAmount(idx, editValue); setEditing(null); } if (e.key === 'Escape') setEditing(null); }}
                onBlur={() => { updateIncomeAmount(idx, editValue); setEditing(null); }}
                autoFocus style={inputStyle} />
            ) : (
              <span style={{ cursor: 'pointer', color: '#8b5cf6' }}
                onClick={() => startEdit(`income_${idx}`, inc.amount)}>
                {fmt(inc.amount)}
              </span>
            )}
            <button onClick={() => removeIncome(idx)}
              style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 16, padding: 0 }}>&times;</button>
          </div>
          <div style={{ ...gainColStyle, color: '#3b82f6' }}>{fmtD(inc.amount)}</div>
        </div>
      ))}

      {/* Add income */}
      <form onSubmit={addIncome} style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <input type="text" value={newIncome.name} onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
          placeholder="Income source..." style={{ flex: 1, background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        <input type="number" value={newIncome.amount} onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
          placeholder="$/mo" step="0.01" style={{ width: 90, background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        <button type="submit" className="manage-btn" disabled={!newIncome.name.trim()} style={{ fontSize: 12 }}>+ Add</button>
      </form>
    </section>
  );
}

export default MonthlyBudgetPanel;
